import { apiFetch } from "../api.js";

/**
 * Allowance / chores web API module — Phase 13.
 *
 * Two domains: chores (parent-managed list of payable tasks) and
 * ledger (kid-tracked credit + debit + transfer events). Buckets
 * (`/buckets/{member_id}`) is a compute-on-render aggregate.
 */

export type ChoreCadence =
  | "one_time"
  | "daily"
  | "weekly"
  | "biweekly"
  | "monthly";

export interface ChoreBucketSplit {
  spend_pct: number;
  save_pct: number;
  give_pct: number;
}

export interface Chore {
  chore_id: string;
  household_id: string;
  member_id: string;
  name: string;
  description?: string;
  value_cents: number;
  bucket_split: ChoreBucketSplit;
  cadence: ChoreCadence;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface ChoresListResponse {
  chores: Chore[];
  count: number;
}

export interface CreateChoreInput {
  member_id: string;
  name: string;
  description?: string;
  value_cents: number;
  bucket_split?: ChoreBucketSplit;
  cadence?: ChoreCadence;
}

export interface UpdateChoreInput {
  name?: string;
  description?: string;
  value_cents?: number;
  bucket_split?: ChoreBucketSplit;
  cadence?: ChoreCadence;
  is_archived?: boolean;
}

export type AllowanceBucket = "spend" | "save" | "give";

export interface AllowanceBucketAllocation {
  spend_cents: number;
  save_cents: number;
  give_cents: number;
}

export type AllowanceLedgerEntry =
  | {
      entry_type: "chore_completion";
      member_id: string;
      entry_id: string;
      timestamp: string;
      chore_id: string;
      value_cents: number;
      bucket_allocation: AllowanceBucketAllocation;
      note?: string;
    }
  | {
      entry_type: "manual_credit";
      member_id: string;
      entry_id: string;
      timestamp: string;
      value_cents: number;
      bucket_allocation: AllowanceBucketAllocation;
      note?: string;
    }
  | {
      entry_type: "manual_debit";
      member_id: string;
      entry_id: string;
      timestamp: string;
      value_cents: number;
      bucket: AllowanceBucket;
      note?: string;
    }
  | {
      entry_type: "bucket_transfer";
      member_id: string;
      entry_id: string;
      timestamp: string;
      value_cents: number;
      from_bucket: AllowanceBucket;
      to_bucket: AllowanceBucket;
      note?: string;
    };

export interface LedgerListResponse {
  entries: AllowanceLedgerEntry[];
  count: number;
}

export interface BucketsResponse {
  member_id: string;
  member_name: string;
  buckets: {
    spend_cents: number;
    save_cents: number;
    give_cents: number;
    total_cents: number;
  };
  lifetime: {
    earned_cents: number;
    spent_cents: number;
  };
  ledger_entry_count: number;
}

export interface CompleteChoreInput {
  chore_id: string;
  occurred_at?: string;
  note?: string;
}

export type ManualLedgerInput =
  | {
      entry_type: "manual_credit";
      value_cents: number;
      bucket_allocation: AllowanceBucketAllocation;
      note?: string;
    }
  | {
      entry_type: "manual_debit";
      value_cents: number;
      bucket: AllowanceBucket;
      note?: string;
    }
  | {
      entry_type: "bucket_transfer";
      value_cents: number;
      from_bucket: AllowanceBucket;
      to_bucket: AllowanceBucket;
      note?: string;
    };

// === chores ============================================================

export function listChores(memberId?: string): Promise<ChoresListResponse> {
  const qs = memberId
    ? `?member_id=${encodeURIComponent(memberId)}`
    : "";
  return apiFetch<ChoresListResponse>(`/v1/allowance/chores${qs}`);
}

export function createChore(input: CreateChoreInput): Promise<Chore> {
  return apiFetch<Chore>("/v1/allowance/chores", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateChore(
  choreId: string,
  input: UpdateChoreInput,
): Promise<Chore> {
  return apiFetch<Chore>(
    `/v1/allowance/chores/${encodeURIComponent(choreId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export function archiveChore(choreId: string): Promise<Chore> {
  return apiFetch<Chore>(
    `/v1/allowance/chores/${encodeURIComponent(choreId)}`,
    { method: "DELETE" },
  );
}

// === ledger + buckets ===================================================

export function listLedger(memberId: string): Promise<LedgerListResponse> {
  return apiFetch<LedgerListResponse>(
    `/v1/allowance/ledger/${encodeURIComponent(memberId)}`,
  );
}

export function completeChore(
  memberId: string,
  input: CompleteChoreInput,
): Promise<AllowanceLedgerEntry> {
  return apiFetch<AllowanceLedgerEntry>(
    `/v1/allowance/ledger/${encodeURIComponent(memberId)}/complete-chore`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export function postManualLedger(
  memberId: string,
  input: ManualLedgerInput,
): Promise<AllowanceLedgerEntry> {
  return apiFetch<AllowanceLedgerEntry>(
    `/v1/allowance/ledger/${encodeURIComponent(memberId)}/manual`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export function getBuckets(memberId: string): Promise<BucketsResponse> {
  return apiFetch<BucketsResponse>(
    `/v1/allowance/buckets/${encodeURIComponent(memberId)}`,
  );
}
